// Shared multi-series chart palettes.
//
// SERIES_PALETTE (individual forecaster lines) is derived from the Okabe-Ito
// colorblind-safe palette: hues remain distinguishable under deuteranopia/
// protanopia. `dark` variants are hand-lightened for dark surfaces; the
// yellow is darkened in light mode for white-background contrast.
//
// COMPARISON_PALETTE (dashed ensemble-model overlays) keeps the legacy hue
// family so dashed-model vs solid-user series do not share hues.

export const SERIES_PALETTE = [
  { light: '#0072B2', dark: '#56B4E9' }, // blue
  { light: '#E69F00', dark: '#F5C242' }, // orange
  { light: '#009E73', dark: '#2FD0A4' }, // bluish green
  { light: '#D55E00', dark: '#F08A4B' }, // vermillion
  { light: '#CC79A7', dark: '#E3A6C7' }, // reddish purple
  { light: '#56B4E9', dark: '#8CD0F5' }, // sky blue
  { light: '#8E7C00', dark: '#F0E442' }, // yellow (darkened for light bg)
  { light: '#6B7280', dark: '#9CA3AF' }, // neutral gray
];

export const COMPARISON_PALETTE = [
  { light: '#0891B2', dark: '#22D3EE' }, // cyan
  { light: '#16A34A', dark: '#4ADE80' }, // emerald
  { light: '#7C3AED', dark: '#C4B5FD' }, // violet
  { light: '#DB2777', dark: '#F472B6' }, // fuchsia
  { light: '#D97706', dark: '#FCD34D' }, // amber
];

export function withAlpha(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Color for the i-th user series: base palette first, then golden-ratio
// generated hues (copied verbatim from ForecastComparisonChart.js getUserColors
// generator so colors keep matching current behavior for index >= 8).
export function seriesColorAt(index) {
  if (index < SERIES_PALETTE.length) return SERIES_PALETTE[index];

  // Generate additional colors using golden ratio for 9+ users
  const goldenRatio = 0.618033988749895;
  let hue = 0.5; // Start at different point to avoid base palette overlap

  const position = index - SERIES_PALETTE.length;
  for (let i = 0; i <= position; i++) {
    hue += goldenRatio;
    hue %= 1;
  }
  const hueDeg = Math.floor(hue * 360);
  return {
    light: `hsl(${hueDeg}, 70%, 50%)`,
    dark: `hsl(${hueDeg}, 55%, 65%)`,
  };
}

// Ensemble insights (VISUAL_IDENTITY.md): drops and neutral pointers in brand
// teal, rises in accent orange (accent-600 in light: accent-500 fails 3:1 on
// the surface), weak pointers gray. Small text (ribbon and peak labels) uses
// the *Text tones, which reach 4.5:1 on the surface and gray-870.
export const INSIGHT_COLORS = {
  drop: { light: '#05788a', dark: '#3aabb8' }, // brand-600 / brand-400
  rise: { light: '#e85e0e', dark: '#ff9151' }, // accent-600 / accent-400
  weak: { light: '#6b7280', dark: '#9ca3af' }, // gray-500 / gray-400
  dropText: { light: '#05788a', dark: '#3aabb8' }, // brand-600 5.0:1 / brand-400 6.2:1
  riseText: { light: '#c64a09', dark: '#ff9151' }, // accent-700 4.7:1 / accent-400 7.6:1
};
